/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react';
import { ReactNode } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const animationMap = {
  up: fadeInUp,
  left: fadeInLeft,
  right: fadeInRight,
  fade: fadeIn,
  scale: scaleIn,
};

interface ScrollRevealProps {
  children: ReactNode;
  direction?: keyof typeof animationMap;
  delay?: number;
  duration?: number;
  className?: string;
}

export default function ScrollReveal({
  children,
  direction = 'up',
  delay = 0,
  duration = 600,
  className,
}: ScrollRevealProps) {
  const { ref, isVisible } = useScrollAnimation();

  const hiddenStyles = css`
    opacity: 0;
  `;

  const visibleStyles = css`
    animation: ${animationMap[direction]} ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms both;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      opacity: 1;
      transform: none;
    }
  `;

  return (
    <div
      ref={ref}
      css={isVisible ? visibleStyles : hiddenStyles}
      className={className}
    >
      {children}
    </div>
  );
}
